// src/activities/recurrence.ts
var WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
var DEFAULT_TIMEZONE = "Europe/Brussels";
var MAX_STEPS = 2e4;
var formatterCache = /* @__PURE__ */ new Map();
function partsFormatter(timeZone) {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}
function toLocalParts(instant, timeZone) {
  const parts = partsFormatter(timeZone).formatToParts(instant);
  const get = (type) => {
    const p = parts.find((x) => x.type === type);
    return p ? Number(p.value) : 0;
  };
  const hour = get("hour") % 24;
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour,
    minute: get("minute"),
    second: get("second")
  };
}
function offsetMsAt(instant, timeZone) {
  const p = toLocalParts(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant.getTime() / 1e3) * 1e3;
}
function fromLocalParts(p, timeZone) {
  const naive = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const DAY = 864e5;
  const offBefore = offsetMsAt(new Date(naive - DAY), timeZone);
  const offAfter = offsetMsAt(new Date(naive + DAY), timeZone);
  const candidates = offBefore === offAfter ? [naive - offBefore] : [naive - offBefore, naive - offAfter];
  const matches = candidates.filter((t) => {
    const got = toLocalParts(new Date(t), timeZone);
    return got.year === p.year && got.month === p.month && got.day === p.day && got.hour === p.hour && got.minute === p.minute && got.second === p.second;
  });
  if (matches.length > 0) return new Date(Math.min(...matches));
  return new Date(naive - offBefore);
}
function asDate(v) {
  if (v instanceof Date) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) throw new TypeError(`Invalid date: ${String(v)}`);
  return d;
}
function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function weekdayIndex(year, month, day) {
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}
function isRecurrenceRule(value) {
  if (!value || typeof value !== "object") return false;
  const freq = value.freq;
  return freq === "daily" || freq === "weekly" || freq === "monthly" || freq === "yearly";
}
function expand(rule, opts) {
  const timezone = opts.timezone || DEFAULT_TIMEZONE;
  const from = asDate(opts.from);
  const to = asDate(opts.to);
  const limit = opts.limit == null ? 1e3 : Math.max(0, Math.floor(opts.limit));
  const anchorRaw = opts.start ?? rule?.start;
  if (anchorRaw == null) {
    throw new TypeError(
      "expand() needs a series anchor: pass opts.start (the first occurrence) or rule.start."
    );
  }
  const anchor = asDate(anchorRaw);
  if (limit === 0 || to.getTime() < from.getTime()) return [];
  const anchorLocal = toLocalParts(anchor, timezone);
  const timeOfDay = {
    hour: anchorLocal.hour,
    minute: anchorLocal.minute,
    second: anchorLocal.second
  };
  if (!isRecurrenceRule(rule)) {
    return anchor >= from && anchor <= to ? [anchor] : [];
  }
  const interval = Math.max(1, Math.floor(rule.interval ?? 1));
  const untilInstant = parseUntil(rule.until, timezone);
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  let produced = 0;
  let steps = 0;
  let cursor = { year: anchorLocal.year, month: anchorLocal.month, day: anchorLocal.day };
  const emit = (year, month, day) => {
    if (day < 1 || day > daysInMonth(year, month)) return "ok";
    const instant = fromLocalParts({ year, month, day, ...timeOfDay }, timezone);
    if (instant.getTime() < anchor.getTime()) return "ok";
    if (untilInstant !== null && instant.getTime() > untilInstant) return "stop";
    produced += 1;
    if (instant.getTime() >= from.getTime() && instant.getTime() <= to.getTime()) {
      if (!seen.has(instant.getTime())) {
        seen.add(instant.getTime());
        out.push(instant);
      }
    }
    if (rule.count != null && produced >= rule.count) return "stop";
    if (out.length >= limit) return "stop";
    if (instant.getTime() > to.getTime()) return "stop";
    return "ok";
  };
  outer: while (steps < MAX_STEPS) {
    steps += 1;
    if (rule.freq === "daily") {
      if (emit(cursor.year, cursor.month, cursor.day) === "stop") break;
      cursor = addDays(cursor, interval);
      continue;
    }
    if (rule.freq === "weekly") {
      const wanted = normaliseWeekdays(rule.byWeekday, cursor, anchorLocal);
      const monday = addDays(cursor, -weekdayIndex(cursor.year, cursor.month, cursor.day));
      for (const wd of wanted) {
        const d = addDays(monday, wd);
        if (emit(d.year, d.month, d.day) === "stop") break outer;
      }
      cursor = addDays(monday, 7 * interval);
      continue;
    }
    if (rule.freq === "monthly") {
      const days = normaliseMonthDays(rule.byMonthDay, anchorLocal.day);
      for (const md of days) {
        const day = md === -1 ? daysInMonth(cursor.year, cursor.month) : md;
        if (emit(cursor.year, cursor.month, day) === "stop") break outer;
      }
      cursor = addMonths({ year: cursor.year, month: cursor.month, day: 1 }, interval);
      continue;
    }
    if (emit(cursor.year, anchorLocal.month, anchorLocal.day) === "stop") break;
    cursor = { year: cursor.year + interval, month: anchorLocal.month, day: anchorLocal.day };
  }
  out.sort((a, b) => a.getTime() - b.getTime());
  return out.slice(0, limit);
}
function parseUntil(until, timezone) {
  if (!until) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(until);
  if (dateOnly) {
    const end = fromLocalParts(
      {
        year: Number(dateOnly[1]),
        month: Number(dateOnly[2]),
        day: Number(dateOnly[3]),
        hour: 23,
        minute: 59,
        second: 59
      },
      timezone
    );
    return end.getTime();
  }
  const d = new Date(until);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}
function normaliseWeekdays(byWeekday, _cursor, anchor) {
  if (!byWeekday || byWeekday.length === 0) {
    return [weekdayIndex(anchor.year, anchor.month, anchor.day)];
  }
  const idx = byWeekday.map((w) => WEEKDAYS.indexOf(w)).filter((i) => i >= 0).sort((a, b) => a - b);
  return idx.length > 0 ? idx : [weekdayIndex(anchor.year, anchor.month, anchor.day)];
}
function normaliseMonthDays(byMonthDay, anchorDay) {
  if (!byMonthDay || byMonthDay.length === 0) return [anchorDay];
  const valid = byMonthDay.map((n) => Math.trunc(n)).filter((n) => n === -1 || n >= 1 && n <= 31);
  const sorted = valid.filter((n) => n !== -1).sort((a, b) => a - b);
  return valid.includes(-1) ? [...sorted, -1] : sorted.length > 0 ? sorted : [anchorDay];
}
function addDays(d, n) {
  const t = new Date(Date.UTC(d.year, d.month - 1, d.day));
  t.setUTCDate(t.getUTCDate() + n);
  return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, day: t.getUTCDate() };
}
function addMonths(d, n) {
  const total = d.year * 12 + (d.month - 1) + n;
  return { year: Math.floor(total / 12), month: total % 12 + 1, day: d.day };
}

export { DEFAULT_TIMEZONE, WEEKDAYS, expand, fromLocalParts, isRecurrenceRule, toLocalParts };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map